import React, { useState, useEffect } from "react";
import { Link, Redirect } from "react-router-dom";
import {
  Col,
  Row,
} from "../../../components/Component";
import { Card } from "reactstrap";
import http from "../../../utils/http-common";
import { getPublicUrl } from "../../../utils/Utils";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import IntegrationsApiKeysPanel from "./components/IntegrationsApiKeysPanel";

const INTEGRATION_LABELS = {
  email: "Email (Gmail OAuth)",
  stripe: "Stripe (payments)",
  stripeWebhook: "Stripe webhook secret",
  visio: "Video (Daily.co / Jitsi)",
  calendly: "Calendly",
  googleCalendar: "Google Calendar",
  yousign: "Yousign (e-signature)",
  yousignWebhook: "Yousign webhook secret",
};

/** Legacy env-style key; Daily.co uses integrations.* above. */
const JITSI_KEYS = [
  { key: "jitsi_app_id", label: "Jitsi App ID (optional)", placeholder: "If using Jitsi instead of Daily.co", sensitive: true },
];

const MASKED_PLACEHOLDER = "••••••••";

/** Boolean Settings keys — default enabled when absent in DB */
const INTEGRATION_ENABLE_KEYS = {
  email: "integrations.emailEnabled",
  stripe: "integrations.stripeEnabled",
  visio: "integrations.visioEnabled",
  calendly: "integrations.calendlyEnabled",
  googleCalendar: "integrations.googleCalendarEnabled",
  yousign: "integrations.yousignEnabled",
};

/** @typedef {"configured" | "incomplete" | "inactive" | "disabled"} IntegrationHealth */

/**
 * @param {IntegrationHealth} state
 */
function statusBadgeProps(state) {
  switch (state) {
    case "configured":
      return { className: "success", label: "Ready" };
    case "incomplete":
      return { className: "warning", label: "Incomplete" };
    case "disabled":
      return { className: "secondary", label: "Off" };
    default:
      return { className: "secondary", label: "Inactive" };
  }
}

/**
 * @param {{ configured?: boolean }} [a]
 * @param {{ configured?: boolean }} [b]
 * @returns {IntegrationHealth}
 */
function pairHealth(a, b) {
  if (a?.enabled === false || b?.enabled === false) return "disabled";
  const x = !!a?.configured;
  const y = !!b?.configured;
  if (x && y) return "configured";
  if (x || y) return "incomplete";
  return "inactive";
}

/**
 * @param {{ configured?: boolean, enabled?: boolean }} [x]
 * @returns {IntegrationHealth}
 */
function singleHealth(x) {
  if (x?.enabled === false) return "disabled";
  return x?.configured ? "configured" : "inactive";
}

/**
 * @param {{ settingsBasePath?: string }} props
 */
export function IntegrationsSettingsSection({ settingsBasePath }) {
  const settingsBase = settingsBasePath || getPublicUrl("admin-settings");
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const emptyJitsi = { jitsi_app_id: "" };
  const [jitsiForm, setJitsiForm] = useState(emptyJitsi);
  const [jitsiSaving, setJitsiSaving] = useState(false);

  const [sectionMessage, setSectionMessage] = useState(null);
  const [toggleKeySaving, setToggleKeySaving] = useState(null);

  const fetchStatus = (options = {}) => {
    const { signal } = options;
    return http
      .get("/api/v1/settings/integrations-status", { signal })
      .then((res) => {
        const data = res.data?.data || null;
        setStatus(data);
        if (data?.visio?.configured) {
          setJitsiForm({ jitsi_app_id: MASKED_PLACEHOLDER });
        }
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setStatus(null);
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  };

  useEffect(() => {
    const ac = new AbortController();
    fetchStatus({ signal: ac.signal });
    return () => ac.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load; fetchStatus closes over latest setters
  }, []);

  const saveIntegrationEnabled = (settingKey, nextEnabled) => {
    setToggleKeySaving(settingKey);
    setSectionMessage(null);
    return http
      .put(`/api/v1/settings/${encodeURIComponent(settingKey)}`, {
        value: nextEnabled,
        label: "Integration enabled",
        description:
          "When disabled, the platform does not use this integration at runtime (stored secrets are kept).",
        category: "integration",
        type: "boolean",
      })
      .then(() => {
        setSectionMessage({ type: "success", section: null, text: "Integration toggle saved." });
        return fetchStatus();
      })
      .catch((err) => {
        setSectionMessage({
          type: "danger",
          section: null,
          text: err.response?.data?.error || err.message || "Failed to save toggle",
        });
      })
      .finally(() => setToggleKeySaving(null));
  };

  /**
   * @param {string} settingKey
   * @param {boolean} [enabled] undefined = treat as enabled (legacy API)
   */
  const renderEnableSwitch = (settingKey, enabled) => {
    const on = enabled !== false;
    return (
      <div className="form-check form-switch mb-2">
        <input
          className="form-check-input"
          type="checkbox"
          id={`toggle-${settingKey.replace(/\./g, "-")}`}
          role="switch"
          checked={on}
          disabled={toggleKeySaving === settingKey}
          onChange={(e) => saveIntegrationEnabled(settingKey, e.target.checked)}
          aria-label="Integration enabled"
        />
        <label className="form-check-label small" htmlFor={`toggle-${settingKey.replace(/\./g, "-")}`}>
          Integration enabled
        </label>
      </div>
    );
  };

  const putSettings = (keys, form, setSaving, section) => {
    setSectionMessage(null);
    setSaving(true);
    const promises = keys.map(({ key }) => {
      const value = form[key];
      if (value == null || String(value).trim() === "" || value === MASKED_PLACEHOLDER) return Promise.resolve();
      return http.put(`/api/v1/settings/${encodeURIComponent(key)}`, {
        value: String(value).trim(),
        label: keys.find((k) => k.key === key)?.label || key,
        category: "integration",
        type: "string",
      });
    });
    return Promise.all(promises)
      .then(() => {
        setSectionMessage({ type: "success", section, text: "Saved." });
        return fetchStatus();
      })
      .catch((err) => {
        setSectionMessage({
          type: "danger",
          section,
          text: err.response?.data?.error || err.message || "Failed to save",
        });
      })
      .finally(() => setSaving(false));
  };

  const renderStatusOverview = () => {
    if (!status) return null;
    const stripeH = pairHealth(status.stripe, status.stripeWebhook);
    const yousignH = pairHealth(status.yousign, status.yousignWebhook);
    const emailH = singleHealth(status.email);
    const visioH = singleHealth(status.visio);
    const calH = singleHealth(status.calendly);
    const gcH = singleHealth(status.googleCalendar);

    const badge = (health) => {
      const { className, label } = statusBadgeProps(health);
      return (
        <span className={`badge badge-dot badge-${className}`}>
          {label}
        </span>
      );
    };

    const detailLine = (ok, text) => (
      <li className={`small ${ok ? "text-success" : "text-soft"}`}>
        {ok ? "✓ " : "○ "}
        {text}
      </li>
    );

    return (
      <>
        <h6 className="overline-title text-primary-alt mb-3">Status overview</h6>
        <Row className="g-gs mb-2">
          <Col md="12" lg="6">
            <h6 className="text-soft mb-2">Payments</h6>
            <Card className="card-bordered h-100">
              <div className="card-inner">
                {renderEnableSwitch(INTEGRATION_ENABLE_KEYS.stripe, status.stripe?.enabled)}
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h6 className="card-title mb-0">{INTEGRATION_LABELS.stripe}</h6>
                  {badge(stripeH)}
                </div>
                <ul className="list-unstyled mb-0">
                  {detailLine(!!status.stripe?.configured, "Stripe secret key")}
                  {detailLine(!!status.stripeWebhook?.configured, "Webhook signing secret")}
                </ul>
                {stripeH === "incomplete" && (
                  <p className="text-soft small mb-0 mt-2">
                    Live payments need both the API secret and a valid webhook secret for reliable payment state.
                  </p>
                )}
              </div>
            </Card>
          </Col>
          <Col md="12" lg="6">
            <h6 className="text-soft mb-2">Video</h6>
            <Card className="card-bordered h-100">
              <div className="card-inner">
                {renderEnableSwitch(INTEGRATION_ENABLE_KEYS.visio, status.visio?.enabled)}
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <h6 className="card-title mb-2">{INTEGRATION_LABELS.visio}</h6>
                    <p className="text-soft small mb-0">
                      Daily.co API key or Jitsi app id for consultation rooms.
                    </p>
                  </div>
                  {badge(visioH)}
                </div>
              </div>
            </Card>
          </Col>
        </Row>
        <Row className="g-gs mb-2">
          <Col md="12">
            <h6 className="text-soft mb-2">Scheduling &amp; calendars</h6>
            <Row className="g-gs">
              <Col md="6" lg="4">
                <Card className="card-bordered h-100">
                  <div className="card-inner">
                    {renderEnableSwitch(INTEGRATION_ENABLE_KEYS.calendly, status.calendly?.enabled)}
                    <div className="d-flex justify-content-between align-items-start">
                      <h6 className="card-title mb-0">{INTEGRATION_LABELS.calendly}</h6>
                      {badge(calH)}
                    </div>
                  </div>
                </Card>
              </Col>
              <Col md="6" lg="4">
                <Card className="card-bordered h-100">
                  <div className="card-inner">
                    {renderEnableSwitch(INTEGRATION_ENABLE_KEYS.googleCalendar, status.googleCalendar?.enabled)}
                    <div className="d-flex justify-content-between align-items-start">
                      <h6 className="card-title mb-0">{INTEGRATION_LABELS.googleCalendar}</h6>
                      {badge(gcH)}
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="g-gs mb-2">
          <Col md="12" lg="6">
            <h6 className="text-soft mb-2">Email</h6>
            <Card className="card-bordered h-100">
              <div className="card-inner">
                {renderEnableSwitch(INTEGRATION_ENABLE_KEYS.email, status.email?.enabled)}
                <div className="d-flex justify-content-between align-items-start">
                  <h6 className="card-title mb-0">{INTEGRATION_LABELS.email}</h6>
                  {badge(emailH)}
                </div>
              </div>
            </Card>
          </Col>
          <Col md="12" lg="6">
            <h6 className="text-soft mb-2">E-signatures</h6>
            <Card className="card-bordered h-100">
              <div className="card-inner">
                {renderEnableSwitch(INTEGRATION_ENABLE_KEYS.yousign, status.yousign?.enabled)}
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <h6 className="card-title mb-0">Yousign</h6>
                  {badge(yousignH)}
                </div>
                <ul className="list-unstyled mb-0">
                  {detailLine(!!status.yousign?.configured, "API key")}
                  {detailLine(!!status.yousignWebhook?.configured, "Webhook HMAC secret")}
                </ul>
                {yousignH === "incomplete" && (
                  <p className="text-soft small mb-0 mt-2">
                    Add the missing secret so signatures and webhook verification both work end-to-end.
                  </p>
                )}
              </div>
            </Card>
          </Col>
        </Row>
      </>
    );
  };

  const renderSecretForm = (title, keys, form, setForm, onSubmit, saving, helpText) => (
    <Card className="card-bordered mt-4">
      <div className="card-inner">
        <h5 className="card-title mb-3">{title}</h5>
        {helpText && <p className="text-soft small mb-3">{helpText}</p>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          {keys.map(({ key, label, placeholder, sensitive }) => (
            <div key={key} className="form-group mb-3">
              <label className="form-label" htmlFor={key}>
                {label}
                {sensitive && form[key] === MASKED_PLACEHOLDER && (
                  <span className="text-soft small ms-2">(saved — enter new value to replace)</span>
                )}
              </label>
              <input
                id={key}
                type={sensitive ? "password" : "text"}
                className="form-control"
                placeholder={placeholder}
                value={form[key] ?? ""}
                onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                autoComplete="off"
              />
            </div>
          ))}
          {sectionMessage && sectionMessage.section === title && (
            <div className={`alert alert-${sectionMessage.type === "success" ? "success" : "danger"} mb-3`} role="alert">
              {sectionMessage.text}
            </div>
          )}
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </button>
        </form>
      </div>
    </Card>
  );

  return (
    <>
      <div className="alert alert-light border mb-4" role="region" aria-label="Related configuration">
        <strong>Teleconsultation product settings</strong> (OTP, cancellation window, document retention, default duration) are under
        the{" "}
        <Link to={`${settingsBase}?tab=teleconsultation`}>Teleconsultation</Link> tab in Settings.
      </div>
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : status ? (
            <>
              {renderStatusOverview()}

              <IntegrationsApiKeysPanel onSaved={() => fetchStatus()} />

              <h6 className="overline-title text-primary-alt mt-4 mb-3">Optional Jitsi</h6>

              {renderSecretForm(
                "Jitsi (optional)",
                JITSI_KEYS,
                jitsiForm,
                setJitsiForm,
                () => putSettings(JITSI_KEYS, jitsiForm, setJitsiSaving, "Jitsi (optional)"),
                jitsiSaving,
                "Only if you use Jitsi instead of Daily.co. Daily.co key is in the API keys section above."
              )}
            </>
          ) : (
            <div className="alert alert-warning" role="alert">
              Unable to load integrations status. Ensure the backend exposes GET /api/v1/settings/integrations-status (admin).
            </div>
          )}
    </>
  );
}

export default function IntegrationsLegacyRedirect() {
  return <Redirect to={{ pathname: getPublicUrl("admin-settings"), search: "?tab=integrations" }} />;
}
